import { ShieldCheck } from 'lucide-react'
import {
  dismissCertTrustNotice,
  useAppDispatch,
  useAppSelector
} from '@axilon-apps/domains-ontology-ui-state'
import { Button } from '../../../components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '../../../components/ui/dialog'

/**
 * One-time notice shown after a GWBK ingest that enabled secured OPC UA tag
 * scraping. The proxy subscribes over an encrypted channel using a client
 * certificate the gateway operator must move from Quarantined to Trusted in
 * Ignition — until then the subscription (and tag readings) stay empty. The
 * notice is driven by `certTrustNotice` in the ingest slice, set by the
 * branch-adoption bridge when the tracked job finishes successfully.
 */
export function CertTrustNoticeDialog() {
  const dispatch = useAppDispatch()
  const notice = useAppSelector((s) => s.ingest.certTrustNotice)

  const handleClose = () => dispatch(dismissCertTrustNotice())

  return (
    <Dialog
      open={notice !== null}
      onOpenChange={(next) => {
        if (!next) handleClose()
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="size-5 text-brand" />
            Trust the OPC UA client certificate
          </DialogTitle>
          <DialogDescription>
            The ingest{notice ? ` of ${notice.fileName}` : ''} enabled secured OPC UA tag scraping.
            Before the proxy can subscribe to this gateway&apos;s tags, an operator must trust its
            client certificate on the Ignition gateway.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 text-sm text-ink-3">
          <p>In the Ignition Gateway web UI:</p>
          <ol className="list-decimal space-y-1 pl-5">
            <li>
              Open <span className="font-medium text-ink">Config → OPC UA → Security</span>.
            </li>
            <li>
              Find the proxy&apos;s certificate under{' '}
              <span className="font-medium text-ink">Quarantined</span> certificates.
            </li>
            <li>
              Move it to <span className="font-medium text-ink">Trusted</span>.
            </li>
          </ol>
          <p className="text-xs text-ink-muted">
            Tag readings stay empty until the certificate is trusted. If the proxy&apos;s
            certificate is later regenerated, a new entry reappears under Quarantined and must be
            trusted again.
          </p>
        </div>
        <DialogFooter>
          <Button onClick={handleClose}>Got it</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default CertTrustNoticeDialog
